import { zodResolver } from '@hookform/resolvers/zod';
import { type Account, type Goal, type GoalRequest, sortCurrencies } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { ChevronRight, PiggyBank } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { type FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet } from '../components/PickerSheet';
import { GoalProgress } from '../components/PlanningCards';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, IconSquare, ListGroup, listRowClass } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Segmented } from '../components/ui/segmented';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { todayIn } from '../lib/dates';
import { useAccounts, useGoal, useGoals } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount, groupAmountInput } from '../lib/money';
import { type GoalFormValues, goalFormSchema, goalFormValues } from '../lib/planning-forms';

/** "Family" or "Personal · Lea", then "By 15 Jan 2027" when there is a target date. */
function useGoalLine() {
  const { t, i18n } = useTranslation();
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  return (goal: Goal) =>
    [
      goal.ownerMemberId === null
        ? t('goals.family')
        : t('goals.personal', { name: goal.ownerName ?? '' }),
      goal.targetDate
        ? t('goals.by', { date: date.format(new Date(`${goal.targetDate}T00:00:00Z`)) })
        : null,
      goal.archived ? t('goals.archived') : null,
    ]
      .filter(Boolean)
      .join(' · ');
}

/** What is left, reached, or the target date passed; plus the monthly amount when it applies. */
function GoalStatus({ goal }: { goal: Goal }) {
  const { t, i18n } = useTranslation();
  if (goal.reached) return <span className="text-[13px] text-accent">{t('goals.reached')}</span>;
  return (
    <span className="flex flex-col text-[13px]">
      <span className="text-muted">
        {t('goals.remaining', {
          amount: formatAmount(goal.remaining, goal.currency, i18n.language),
        })}
      </span>
      {goal.late ? (
        <span className="text-expense">{t('goals.late')}</span>
      ) : (
        goal.monthlyNeeded && (
          <span className="font-medium text-fg">
            {t('goals.perMonth', {
              amount: formatAmount(goal.monthlyNeeded, goal.currency, i18n.language),
            })}
          </span>
        )
      )}
    </span>
  );
}

/** Savings goals: each with its progress; archived ones on request. */
export function GoalsPage() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const goals = useGoals(showArchived);
  const line = useGoalLine();
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('goals.title')}
        back="/"
        action={
          <Link to="/goals/new" className={buttonVariants({ variant: 'small' })}>
            {t('goals.add')}
          </Link>
        }
      />
      {goals.isError && <FormError error={goals.error} />}
      {!goals.data ? (
        !goals.isError && <p role="status">{t('app.loading')}</p>
      ) : goals.data.items.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{t('goals.empty')}</p>
          <Link to="/goals/new" className={buttonVariants()}>
            {t('goals.add')}
          </Link>
        </Card>
      ) : (
        <ListGroup>
          {goals.data.items.map((goal) => (
            <li key={goal.id}>
              <Link to={`/goals/${goal.id}`} className={`${listRowClass} items-start`}>
                <IconSquare>
                  <PiggyBank className="size-5" strokeWidth={2} />
                </IconSquare>
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="truncate text-[15px] font-medium">{goal.name}</span>
                  <span className="truncate text-xs text-muted">{line(goal)}</span>
                  <GoalProgress goal={goal} />
                  <GoalStatus goal={goal} />
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="mt-2 size-5 shrink-0 text-muted rtl:rotate-180"
                />
              </Link>
            </li>
          ))}
        </ListGroup>
      )}
      <Button variant="ghost" block onClick={() => setShowArchived(!showArchived)}>
        {showArchived ? t('goals.hideArchived') : t('goals.showArchived')}
      </Button>
    </div>
  );
}

/** One goal: progress, the monthly amount, the linked account; change or archive it. */
export function GoalDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const goal = useGoal(id);
  const line = useGoalLine();
  const invalidate = useInvalidateLedger();
  const archive = useMutation({
    mutationFn: (archived: boolean) => api.goals.setArchived(id, archived),
    onSuccess: invalidate,
  });
  if (!goal.data) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('goals.title')} back="/goals" />
        {goal.isError ? <FormError error={goal.error} /> : <p role="status">{t('app.loading')}</p>}
      </div>
    );
  }
  const g = goal.data;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={g.name} back="/goals" />
      <section className="flex flex-col gap-3 rounded-3xl bg-hero p-5 text-hero-fg">
        <span className="text-[13px] text-hero-muted">{line(g)}</span>
        <span
          dir="ltr"
          className="text-[38px] leading-tight font-semibold tracking-[-0.5px] tabular-nums"
        >
          {formatAmount(g.saved, g.currency, i18n.language)}
        </span>
        <span dir="ltr" className="text-[13px] text-hero-muted tabular-nums">
          {t('goals.saved', {
            saved: formatAmount(g.saved, g.currency, i18n.language),
            target: formatAmount(g.targetAmount, g.currency, i18n.language),
          })}{' '}
          · {g.percent}%
        </span>
        <div
          className="h-2 overflow-hidden rounded-full bg-line"
          role="progressbar"
          aria-label={t('goals.progress')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Number.parseInt(g.percent, 10)}
        >
          <div className="h-full rounded-full bg-accent" style={{ width: `${g.percent}%` }} />
        </div>
      </section>
      <Card className="flex flex-col gap-2">
        <GoalStatus goal={g} />
      </Card>
      <ListGroup>
        <li>
          <Link to={`/accounts/${g.accountId}`} className={listRowClass}>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[13px] text-muted">{t('goals.account')}</span>
              <span className="truncate text-[15px] font-medium">{g.accountName}</span>
            </span>
            <ChevronRight
              aria-hidden="true"
              className="size-5 shrink-0 text-muted rtl:rotate-180"
            />
          </Link>
        </li>
      </ListGroup>
      <p className="px-1 text-xs text-muted">
        {t('goals.progressHint', { account: g.accountName, currency: g.currency })}
      </p>
      <FormError error={archive.error} />
      {g.canManage && (
        <div className="grid grid-cols-2 gap-2">
          <Link to={`/goals/${g.id}/edit`} className={buttonVariants({ variant: 'secondary' })}>
            {t('goals.edit')}
          </Link>
          <Button
            variant="secondary"
            disabled={archive.isPending}
            onClick={() => archive.mutate(!g.archived)}
          >
            {g.archived ? t('goals.unarchive') : t('goals.archive')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Accounts a goal can be linked to: cash, bank and wallet accounts that are not archived. */
const savingsAccounts = (accounts: Account[]) =>
  accounts.filter((a) => a.type === 'asset' && a.archivedAt === null);

/** New goal (/goals/new) or change one (/goals/:id/edit). */
export function GoalFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const me = useMe();
  const accounts = useAccounts();
  const goal = useGoal(id ?? '', id !== undefined);
  if (!me.data || !accounts.data || (id !== undefined && !goal.data)) {
    if (accounts.isError || goal.isError) return <FormError error={accounts.error ?? goal.error} />;
    return <p role="status">{t('app.loading')}</p>;
  }
  const options = savingsAccounts(accounts.data.items);
  if (options.length === 0) {
    return (
      <Card className="flex flex-col gap-4">
        <p className="text-muted">{t('goals.noAccounts')}</p>
        <Link to="/accounts/new" className={buttonVariants()}>
          {t('add.noAccountsAction')}
        </Link>
      </Card>
    );
  }
  return (
    <GoalForm
      key={goal.data?.id ?? 'new'}
      goal={goal.data}
      accounts={options}
      baseCurrency={me.data.family.baseCurrency}
      today={todayIn(me.data.family.timezone)}
    />
  );
}

function GoalForm({
  goal,
  accounts,
  baseCurrency,
  today,
}: {
  goal: Goal | undefined;
  accounts: Account[];
  baseCurrency: string;
  today: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const canFamilyWide = useCan('budgets.manage');
  const [picking, setPicking] = useState(false);
  const first = accounts[0]!;
  const resolver = useMemo(() => zodResolver(goalFormSchema()), []);
  const form = useForm<GoalFormValues, unknown, GoalRequest>({
    resolver,
    defaultValues: goalFormValues(goal, {
      accountId: first.id,
      currency: first.balances[0]?.currency ?? baseCurrency,
    }),
  });
  const v = useWatch({ control: form.control }) as GoalFormValues;
  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency: v.currency }) : null;
  const account = accounts.find((a) => a.id === v.accountId);
  const currencies = sortCurrencies(account?.balances.map((b) => b.currency) ?? [], baseCurrency);
  const firstCurrency = currencies[0];
  const currencyFits = currencies.includes(v.currency);
  useEffect(() => {
    if (!currencyFits && firstCurrency) form.setValue('currency', firstCurrency);
  }, [currencyFits, firstCurrency, form]);

  const save = useMutation({
    mutationFn: (body: GoalRequest) =>
      goal ? api.goals.update(goal.id, body) : api.goals.create(body),
    onSuccess: async (saved) => {
      await invalidate();
      navigate(`/goals/${saved.id}`);
    },
  });
  const reformat = () => {
    const parsed = parsePositive(form.getValues('targetAmount'), v.currency);
    if (parsed) form.setValue('targetAmount', groupAmountInput(parsed, v.currency));
  };
  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <PageHeader
        title={goal ? t('goals.editTitle') : t('goals.newTitle')}
        back={goal ? `/goals/${goal.id}` : '/goals'}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="goal-name" className="text-[13px] font-medium text-muted">
          {t('goals.name')}
        </label>
        <Input
          id="goal-name"
          autoComplete="off"
          maxLength={60}
          placeholder={t('goals.namePlaceholder')}
          aria-invalid={errors.name ? true : undefined}
          {...form.register('name')}
        />
        {errors.name && <p className="text-sm text-danger">{errorText(errors.name)}</p>}
      </div>
      <Card className="flex flex-col items-center gap-3">
        <label htmlFor="goal-target" className="text-[13px] text-muted">
          {t('goals.target')}
        </label>
        <input
          id="goal-target"
          inputMode={v.currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={v.currency === 'LBP' ? '50m' : '0.00'}
          aria-invalid={errors.targetAmount ? true : undefined}
          className="w-full bg-transparent text-center text-[46px] leading-tight font-semibold tracking-[-1px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...form.register('targetAmount', { onBlur: reformat })}
        />
        {currencies.length > 1 ? (
          <div className="w-40">
            <Segmented
              label={t('goals.currency')}
              options={currencies.map((c) => ({ value: c, label: c }))}
              value={v.currency}
              onChange={(c) => form.setValue('currency', c)}
            />
          </div>
        ) : (
          <span className="rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">
            {v.currency}
          </span>
        )}
        {errors.targetAmount && (
          <p className="text-sm text-danger">{errorText(errors.targetAmount)}</p>
        )}
      </Card>
      <ListGroup>
        <Row label={t('goals.account')} error={errorText(errors.accountId)}>
          <Chip onClick={() => setPicking(true)}>{account?.name ?? t('goals.pickAccount')}</Chip>
        </Row>
        <Row label={t('goals.targetDate')} htmlFor="goal-date" error={errorText(errors.targetDate)}>
          <input
            id="goal-date"
            type="date"
            min={today}
            aria-describedby="goal-date-hint"
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('targetDate')}
          />
        </Row>
      </ListGroup>
      <p id="goal-date-hint" className="px-1 text-xs text-muted">
        {t('goals.targetDateHint')}
      </p>
      {canFamilyWide && (
        <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-3xl border border-line bg-surface px-4 py-3">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-medium">{t('goals.familyWide')}</span>
            <span id="goal-family-hint" className="text-sm text-muted">
              {t('goals.familyWideHint')}
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-describedby="goal-family-hint"
            className="size-5 shrink-0 accent-accent"
            {...form.register('familyWide')}
          />
        </label>
      )}
      <FormError error={save.error} />
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : t('goals.save')}
      </Button>
      {picking && (
        <PickerSheet
          title={t('goals.pickAccount')}
          options={accounts.map((a) => ({
            value: a.id,
            label: a.name,
            hint: a.balances
              .map((b) => formatAmount(b.balance, b.currency, i18n.language))
              .join(' · '),
          }))}
          selected={v.accountId}
          onPick={(value) => {
            form.setValue('accountId', value);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </form>
  );
}
